// client/src/contexts/TenantContext.tsx
// Loads and exposes the tenant configuration from the platform backend.
// Also resolves frontend-only tenant branding from the hostname.
//
// Auth strategy:
//   /tenant-config is a PROTECTED endpoint — the API Gateway requires a valid
//   Firebase JWT. This context therefore sits INSIDE AuthProvider in the tree
//   (see main.tsx) and uses useAuth() to gate the fetch on a valid user.
//
//   Fetch flow:
//   1. Wait for AuthProvider to resolve (loading === false).
//   2. If user is authenticated, call GET /tenant-config via apiClient (which
//      automatically attaches the Bearer token from Firebase).
//   3. Store the result and apply tenant branding via CSS custom properties.
//   4. If the user is not authenticated yet, stay idle (tenant = null,
//      loading = false) — the login page has access to only the tenantId
//      claim embedded in the token once the user signs in.
//
// Branding resolution:
//   TenantBranding is resolved synchronously from window.location.hostname at
//   context init, independently of the auth state or the /tenant-config fetch.
//   This means logo/title/favicon are available immediately — even on the
//   login page before the user authenticates.
//
// Components that need branding import useTenant():
//   const { tenant, branding, loading, hasFeature } = useTenant();

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { apiClient } from '@/lib/apiClient';
import { useAuth } from '@/contexts/AuthContext';
import { resolveTenantFromHostname } from '@/branding/index';
import type { TenantBranding } from '@/branding/types';

// ── Types ────────────────────────────────────────────────────────────────────

/** Tenant configuration as returned by GET /tenant-config (platform backend). */
export interface TenantConfig {
  /** Stable tenant identifier (e.g. 'icaros'). */
  id: string;

  /** Human-readable display name (e.g. 'ICAROS Aviación'). */
  name: string;

  /** BCP-47 locale (e.g. 'es', 'en'). */
  locale: string;

  /** URL of the tenant logo — used in the sidebar and login page. */
  logoUrl?: string;

  /**
   * Feature flags enabled for this tenant.
   * The UI gates features on these flags so unused modules are hidden.
   * Examples: ['ai_chat', 'quizzes', 'exams', 'calendar']
   */
  features: string[];
}

/** Platform/BFF may return { status, tenant } or the tenant at root. Normalise to TenantConfig. */
function normaliseTenantConfig(raw: unknown): TenantConfig {
  const obj = raw as { tenant?: TenantConfig; id?: string; name?: string; locale?: string; logoUrl?: string; features?: string[] | Record<string, unknown> };
  const tenant = obj.tenant ?? obj;
  if (!tenant || typeof tenant.id !== 'string') {
    throw new Error('Invalid tenant config: missing id');
  }
  const features = Array.isArray(tenant.features)
    ? tenant.features
    : typeof tenant.features === 'object' && tenant.features !== null
      ? Object.keys(tenant.features)
      : [];
  return {
    id: tenant.id,
    name: tenant.name ?? tenant.id,
    locale: tenant.locale ?? 'es',
    logoUrl: tenant.logoUrl,
    features,
  };
}

interface TenantContextValue {
  /** The loaded tenant config from the backend. Null while loading or if the user is not yet authenticated. */
  tenant: TenantConfig | null;

  /**
   * Frontend-only tenant branding resolved from the hostname.
   * Always non-null — falls back to agocBranding if the hostname is unknown.
   * Available immediately before the user authenticates.
   */
  branding: TenantBranding;

  /** True only while an in-flight /tenant-config fetch is running. */
  loading: boolean;

  /** Non-null if the fetch failed (network error, 401, 404…). */
  error: string | null;

  /** Returns true if the given feature flag is enabled for this tenant. */
  hasFeature: (flag: string) => boolean;
}

// ── Branding helpers ─────────────────────────────────────────────────────────

/** Update document title and favicon from branding. Called once after branding is resolved. */
function applyBranding(branding: TenantBranding): void {
  // Page title
  document.title = branding.appTitle;

  // Favicon — update the <link rel="icon"> element (or create it if missing)
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = branding.favicon;
}

// ── Theme application ────────────────────────────────────────────────────────

/**
 * Writes tenant-specific values onto :root CSS custom properties.
 * Called once after a successful /tenant-config fetch.
 * Extend when the backend tenant model grows to include colour overrides.
 */
function applyTenantTheme(tenant: TenantConfig): void {
  const root = document.documentElement;

  // Override page title with backend name if it differs from branding
  document.title = tenant.name;

  // Locale attribute (picked up by screen readers and i18n libraries)
  root.setAttribute('lang', tenant.locale);

  // Future: if platform backend includes theme tokens, apply them here:
  //   root.style.setProperty('--color-primary', tenant.theme.primaryHsl);
}

// ── Context ──────────────────────────────────────────────────────────────────

const TenantContext = createContext<TenantContextValue | null>(null);

export function TenantProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  // TenantProvider must be a child of AuthProvider so useAuth() works here.
  const { user, loading: authLoading } = useAuth();

  // Branding is resolved synchronously from hostname — always available immediately.
  const [branding] = useState<TenantBranding>(() => {
    const resolved = resolveTenantFromHostname(window.location.hostname);
    // Apply immediately so the title/favicon are correct even during auth loading.
    applyBranding(resolved);
    return resolved;
  });

  const [tenant, setTenant] = useState<TenantConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadTenantConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // apiClient automatically attaches the Firebase Bearer token.
      // BFF/platform may return { status, tenant } or the tenant at root — normalise.
      const raw = await apiClient.get<unknown>('/tenant-config');
      const config = normaliseTenantConfig(raw);

      setTenant(config);
      applyTenantTheme(config);

      console.log('[TenantContext] Loaded config for tenant:', config.id);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load tenant config';
      setError(message);
      console.error('[TenantContext] Failed to load tenant config:', message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Do nothing until Firebase has resolved auth state.
    if (authLoading) return;

    // Wait for the platform user (claims-resolved) before fetching tenant config.
    // This ensures tenantId is available in the JWT when /tenant-config is called.
    if (!user) {
      setTenant(null);
      return;
    }

    void loadTenantConfig();
    // Re-fetch if the authenticated user changes (e.g. different tenantId after re-login).
  }, [user?.uid, authLoading, loadTenantConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasFeature = useCallback(
    (flag: string): boolean => tenant?.features.includes(flag) ?? false,
    [tenant],
  );

  const value: TenantContextValue = { tenant, branding, loading, error, hasFeature };

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  );
}

/** Hook — must be used inside TenantProvider (which must be inside AuthProvider). */
export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) {
    throw new Error('[useTenant] Must be used inside <TenantProvider>');
  }
  return ctx;
}
